import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE as API } from '../lib/config';
import { apiWithAuth } from '../lib/api';
import {
  WalletIcon,
  BanknotesIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyYenIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

// Use centralized API base that supports both local and production
// This avoids undefined NEXT_PUBLIC_BACKEND_URL causing broken URLs

// 主要な日本の銀行一覧（コード付き）
const JAPANESE_BANKS_WITH_CODES = [
  { name: "三菱UFJ銀行", code: "0005" },
  { name: "みずほ銀行", code: "0001" },
  { name: "三井住友銀行", code: "0009" },
  { name: "りそな銀行", code: "0010" },
  { name: "埼玉りそな銀行", code: "0017" },
  { name: "ゆうちょ銀行", code: "9900" },
  { name: "楽天銀行", code: "0036" },
  { name: "PayPay銀行", code: "0033" },
  { name: "住信SBIネット銀行", code: "0038" },
  { name: "イオン銀行", code: "0040" },
  { name: "セブン銀行", code: "0034" },
  { name: "ソニー銀行", code: "0035" },
  { name: "GMOあおぞらネット銀行", code: "0042" },
  { name: "auじぶん銀行", code: "0039" },
  { name: "UI銀行", code: "0043" },
  { name: "横浜銀行", code: "0138" },
  { name: "千葉銀行", code: "0134" },
  { name: "福岡銀行", code: "0177" },
  { name: "静岡銀行", code: "0149" },
  { name: "常陽銀行", code: "0130" },
  { name: "北海道銀行", code: "0116" },
  { name: "青森銀行", code: "0117" },
  { name: "みちのく銀行", code: "0118" },
  { name: "秋田銀行", code: "0119" },
  { name: "山形銀行", code: "0122" },
  { name: "岩手銀行", code: "0123" },
  { name: "七十七銀行", code: "0125" },
  { name: "東邦銀行", code: "0126" },
  { name: "群馬銀行", code: "0128" },
  { name: "足利銀行", code: "0129" },
  { name: "筑波銀行", code: "0131" },
  { name: "武蔵野銀行", code: "0132" },
  { name: "山梨中央銀行", code: "0140" },
  { name: "八十二銀行", code: "0142" },
  { name: "北陸銀行", code: "0143" },
  { name: "北國銀行", code: "0145" },
  { name: "福井銀行", code: "0146" },
  { name: "岐阜銀行", code: "0147" },
  { name: "大垣共立銀行", code: "0148" },
  { name: "十六銀行", code: "0150" },
  { name: "愛知銀行", code: "0153" },
  { name: "名古屋銀行", code: "0154" },
  { name: "中京銀行", code: "0155" },
  { name: "百五銀行", code: "0157" },
  { name: "滋賀銀行", code: "0158" },
  { name: "京都銀行", code: "0158" },
  { name: "関西みらい銀行", code: "0159" },
  { name: "池田泉州銀行", code: "0160" },
  { name: "南都銀行", code: "0161" },
  { name: "紀陽銀行", code: "0162" },
  { name: "鳥取銀行", code: "0164" },
  { name: "山陰合同銀行", code: "0165" },
  { name: "中国銀行", code: "0166" },
  { name: "広島銀行", code: "0167" },
  { name: "山口銀行", code: "0168" },
  { name: "阿波銀行", code: "0169" },
  { name: "百十四銀行", code: "0170" },
  { name: "伊予銀行", code: "0172" },
  { name: "四国銀行", code: "0173" },
  { name: "西日本シティ銀行", code: "0178" },
  { name: "佐賀銀行", code: "0180" },
  { name: "十八親和銀行", code: "0181" },
  { name: "肥後銀行", code: "0182" },
  { name: "大分銀行", code: "0183" },
  { name: "宮崎銀行", code: "0184" },
  { name: "鹿児島銀行", code: "0185" },
  { name: "琉球銀行", code: "0187" },
  { name: "沖縄銀行", code: "0188" }
];

// -------- Utility: Japanese normalization (very simple) --------
const toHiragana = (str = '') => {
  return str.replace(/[ァ-ン]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0x60));
};
const normalizeJa = (str = '') => toHiragana((str || '').toLowerCase());

const WalletManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // URLパラメーターからタブを取得
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'overview';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [withdrawalInfo, setWithdrawalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [bankInputMode, setBankInputMode] = useState('dropdown');
  const [branchInputMode, setBranchInputMode] = useState('dropdown');

  // 銀行検索関連の状態
  const [bankSearchQuery, setBankSearchQuery] = useState('');
  const [bankSearchResults, setBankSearchResults] = useState([]);
  const [showBankDropdown, setShowBankDropdown] = useState(false);

  // ひらがなサジェスト関連の状態
  const [hiraganaMode, setHiraganaMode] = useState(true); // デフォルトでひらがなモード
  const [bankHiraganaSuggestions, setBankHiraganaSuggestions] = useState([]);
  const [branchHiraganaSuggestions, setBranchHiraganaSuggestions] = useState([]);
  const [showBankHiraganaSuggestions, setShowBankHiraganaSuggestions] = useState(false);
  const [showBranchHiraganaSuggestions, setShowBranchHiraganaSuggestions] = useState(false);

  // 支店検索関連の状態
  const [branchSearchQuery, setBranchSearchQuery] = useState('');
  const [branchSearchResults, setBranchSearchResults] = useState([]);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [selectedBankName, setSelectedBankName] = useState('');

  // 口座照合関連の状態
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  // Stripe Connect関連の状態
  const [stripeConnectAccount, setStripeConnectAccount] = useState(null);
  const [isStripeConnecting, setIsStripeConnecting] = useState(false);

  // 本人確認状態の管理
  const [identityStatus, setIdentityStatus] = useState(null);

  // フォーム状態
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    bank_code: '',
    branch_name: '',
    branch_code: '',
    account_type: '普通',
    account_number: '',
    account_holder_name: ''
  });

  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    bank_account_id: ''
  });

  useEffect(() => {
    if (!user || user.user_type !== 'job_seeker') {
      navigate('/');
      return;
    }

    fetchWalletData();
    // listen for instant credit from employer completion
    const onCredit = (e) => {
      const tx = e?.detail;
      if (!tx) return;
      try {
        // keep local session copy for robustness
        const key = 'local_wallet_tx';
        const list = JSON.parse(sessionStorage.getItem(key) || '[]');
        list.unshift(tx);
        sessionStorage.setItem(key, JSON.stringify(list.slice(0, 100)));
      } catch (_) { }
      setTransactions(prev => [tx, ...(prev || [])]);
      setWallet(prev => prev ? { ...prev, balance: (prev.balance || 0) + (tx.net_amount || tx.amount || 0) } : prev);
      toast.success('報酬がウォレットに反映されました');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('wallet:credit', onCredit);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('wallet:credit', onCredit);
      }
    };
  }, [user, navigate]);

  // 銀行検索機能
  const searchBanks = async (query) => {
    try {
      const q = encodeURIComponent(query);
      // 1) legacy search
      let response = await fetch(`${API}/banks/search?q=${q}`);
      let banks = [];
      if (response.ok) {
        const data = await response.json();
        banks = data?.banks || [];
      }
      // 2) fallback to accurate search if empty
      if (!banks.length) {
        response = await fetch(`${API}/banks/accurate-search?q=${q}`);
        if (response.ok) {
          const data = await response.json();
          banks = data?.banks || [];
        }
      }
      // 3) client-side fallback from static list
      if (!banks.length) {
        const nq = normalizeJa(query);
        banks = JAPANESE_BANKS_WITH_CODES
          .filter(b => normalizeJa(b.name).includes(nq))
          .map(b => ({ name: b.name, code: b.code }));
      }
      setBankSearchResults(banks);
    } catch (error) {
      console.error('Error searching banks:', error);
    }
  };

  // 銀行検索入力の処理
  const handleBankSearchInput = (e) => {
    const query = e.target.value;
    setBankSearchQuery(query);
    setBankForm({ ...bankForm, bank_name: query, branch_name: '' });

    // 銀行名が変わったら支店と照合結果をリセット
    setSelectedBankName('');
    setBranchSearchQuery('');
    setBranchSearchResults([]);
    setShowBranchDropdown(false);
    setVerificationResult(null);

    if (query.length > 0) {
      searchBanks(query);
      setShowBankDropdown(true);
    } else {
      setShowBankDropdown(false);
      setBankSearchResults([]);
    }
  };

  // 銀行選択の処理
  const selectBank = (bank) => {
    const bankObj = typeof bank === 'object' ? bank : { name: bank };
    const bankName = bankObj.name;
    const bankCode = bankObj.code || bankObj.bank_code || '';
    setBankForm({ ...bankForm, bank_name: bankName, bank_code: bankCode, branch_name: '', branch_code: '' });
    setBankSearchQuery(bankName);
    setSelectedBankName(bankName);
    setShowBankDropdown(false);

    // 支店検索の状態をリセット
    setBranchSearchQuery('');
    setBranchSearchResults([]);
    setShowBranchDropdown(false);
    setVerificationResult(null);

    // 選択された銀行の全支店を取得
    if (bankName) {
      searchBranches(bankName, '');
      // 支店選択モードを自動で「選択」に変更
      setBranchInputMode('dropdown');
    }
  };

  // 支店検索機能
  const searchBranches = async (bankName, query) => {
    try {
      const encodedBankName = encodeURIComponent(bankName);
      const response = await fetch(`${API}/banks/${encodedBankName}/branches?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setBranchSearchResults(data.branches);
      }
    } catch (error) {
      console.error('Error searching branches:', error);
    }
  };

  // 支店検索入力の処理
  const handleBranchSearchInput = (e) => {
    const query = e.target.value;
    setBranchSearchQuery(query);
    setBankForm({ ...bankForm, branch_name: query });

    if (selectedBankName && query.length > 0) {
      searchBranches(selectedBankName, query);
      setShowBranchDropdown(true);
    } else if (selectedBankName && query.length === 0) {
      // 空の場合は全支店を取得
      searchBranches(selectedBankName, '');
      setShowBranchDropdown(true);
    } else {
      setShowBranchDropdown(false);
      setBranchSearchResults([]);
    }

    // 支店名が変更されたら照合結果をリセット
    setVerificationResult(null);
  };

  // 支店選択の処理
  const selectBranch = (branch) => {
    const branchObj = typeof branch === 'object' ? branch : { name: branch };
    const branchName = branchObj.name;
    const branchCode = branchObj.code || branchObj.branch_code || '';
    setBankForm({ ...bankForm, branch_name: branchName, branch_code: branchCode });
    setBranchSearchQuery(branchName);
    setShowBranchDropdown(false);
    setVerificationResult(null);
  };

  // 口座照合機能
  const verifyAccount = async () => {
    if (!bankForm.bank_name || !bankForm.branch_name || !bankForm.account_number || !bankForm.account_holder_name) {
      toast.error('すべての項目を入力してください');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(`${API}/banks/verify-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bank_name: bankForm.bank_name,
          branch_name: bankForm.branch_name,
          account_type: bankForm.account_type,
          account_number: bankForm.account_number,
          account_holder_name: bankForm.account_holder_name
        })
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationResult(data);

        if (data.is_valid) {
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error('口座照合に失敗しました');
      }
    } catch (error) {
      console.error('Error verifying account:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setIsVerifying(false);
    }
  };

  // Stripe Connect関連の関数
  const createStripeConnectAccount = async () => {
    try {
      setIsStripeConnecting(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const { data } = await client.post('/wallet/stripe-connect', {});
      setStripeConnectAccount(data);
      if (data?.onboarding_url) window.location.href = data.onboarding_url;
    } catch (error) {
      console.error('Error creating Stripe Connect account:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setIsStripeConnecting(false);
    }
  };

  const fetchStripeConnectAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const { data } = await client.get('/wallet/stripe-connect');
      setStripeConnectAccount(data);
    } catch (error) {
      console.error('Error fetching Stripe Connect account:', error);
    }
  };

  // ひらがなサジェスト用の検索関数
  const searchBanksHiragana = async (query) => {
    if (!query || query.length < 1) {
      setBankHiraganaSuggestions([]);
      return;
    }

    try {
      const q = encodeURIComponent(query);
      let response = await fetch(`${API}/banks/hiragana-search?q=${q}`);
      let banks = [];
      if (response.ok) {
        const data = await response.json();
        banks = data?.banks || [];
      }
      if (!banks.length) {
        response = await fetch(`${API}/banks/accurate-search?q=${q}`);
        if (response.ok) {
          const data = await response.json();
          banks = data?.banks || [];
        }
      }
      if (!banks.length) {
        const nq = normalizeJa(query);
        banks = JAPANESE_BANKS_WITH_CODES
          .filter(b => normalizeJa(b.name).includes(nq))
          .map(b => ({ name: b.name, code: b.code }));
      }
      setBankHiraganaSuggestions(banks);
    } catch (error) {
      console.error('Error searching banks with hiragana:', error);
      setBankHiraganaSuggestions([]);
    }
  };

  const searchBranchesHiragana = async (bankName, query) => {
    if (!bankName) {
      setBranchHiraganaSuggestions([]);
      return;
    }

    try {
      const encodedBankName = encodeURIComponent(bankName);
      const encodedQuery = encodeURIComponent(query || '');
      let response = await fetch(`${API}/banks/${encodedBankName}/branches/hiragana-search?q=${encodedQuery}`);
      let branches = [];
      if (response.ok) {
        const data = await response.json();
        branches = data?.branches || [];
      }
      // fallback: use normal branches; if kana metadata is unavailable, show all so user can pick manually
      if (!branches.length) {
        response = await fetch(`${API}/banks/${encodedBankName}/branches?q=`);
        if (response.ok) {
          const data = await response.json();
          branches = data?.branches || [];
        }
      }
      setBranchHiraganaSuggestions(branches);
    } catch (error) {
      console.error('Error searching branches with hiragana:', error);
      setBranchHiraganaSuggestions([]);
    }
  };

  // ひらがな入力ハンドラー
  const handleBankHiraganaInput = (e) => {
    const value = e.target.value;
    setBankSearchQuery(value);
    setBankForm({ ...bankForm, bank_name: value, branch_name: '' });

    // 支店の状態をリセット
    setBranchSearchQuery('');
    setBranchHiraganaSuggestions([]);
    setSelectedBankName('');
    setVerificationResult(null);

    // ひらがな検索を実行
    searchBanksHiragana(value);
    setShowBankHiraganaSuggestions(value.length > 0);
  };

  const handleBranchHiraganaInput = (e) => {
    const value = e.target.value;
    setBranchSearchQuery(value);
    setBankForm({ ...bankForm, branch_name: value });
    setVerificationResult(null);

    // ひらがな検索を実行
    if (selectedBankName) {
      searchBranchesHiragana(selectedBankName, value);
      setShowBranchHiraganaSuggestions(value.length > 0);
    }
  };

  // サジェスト選択ハンドラー
  const selectBankFromHiraganaSuggestion = (bank) => {
    setBankForm({ ...bankForm, bank_name: bank.name, bank_code: bank.code || bank.bank_code || '', branch_name: '', branch_code: '' });
    setBankSearchQuery(bank.name);
    setSelectedBankName(bank.name);
    setShowBankHiraganaSuggestions(false);

    // 選択した銀行の支店を検索
    setBranchSearchQuery('');
    setBranchHiraganaSuggestions([]);
    searchBranchesHiragana(bank.name, '');
    setVerificationResult(null);
  };

  const selectBranchFromHiraganaSuggestion = (branch) => {
    setBankForm({ ...bankForm, branch_name: branch.name, branch_code: branch.code || branch.branch_code || '' });
    setBranchSearchQuery(branch.name);
    setShowBranchHiraganaSuggestions(false);
    setVerificationResult(null);
  };

  // カタカナバリデーション
  const isValidKatakana = (str) => {
    const katakanaRegex = /^[ァ-ヶー\s]+$/;
    return katakanaRegex.test(str);
  };

  // 口座番号バリデーション
  const isValidAccountNumber = (str) => {
    const accountRegex = /^\d{7}$/;
    return accountRegex.test(str);
  };

  // ウォレットデータ取得
  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      // 並行してデータ取得（出金情報も追加）
      const [walletRes, transactionsRes, bankAccountsRes, withdrawalRes, withdrawalInfoRes] = await Promise.all([
        client.get('/wallet').catch(() => ({ data: null })),
        client.get('/wallet/transactions').catch(() => ({ data: [] })),
        client.get('/wallet/bank-accounts').catch(() => ({ data: [] })),
        client.get('/wallet/withdrawal-requests').catch(() => ({ data: [] })),
        client.get('/wallet/withdrawal-info').catch(() => ({ data: null })),
      ]);

      if (walletRes && walletRes.data) setWallet(walletRes.data);
      if (transactionsRes) setTransactions(transactionsRes.data || []);
      // merge local session txs for local E2E
      try {
        const localList = JSON.parse(sessionStorage.getItem('local_wallet_tx') || '[]');
        if (Array.isArray(localList) && localList.length) {
          setTransactions(prev => [...localList, ...(prev || [])]);
        }
      } catch (_) { }
      if (bankAccountsRes) {
        const bankAccountsData = bankAccountsRes.data || [];
        setBankAccounts(bankAccountsData);
        if (bankAccountsData.length > 0 && !withdrawForm.bank_account_id) {
          setWithdrawForm(prev => ({ ...prev, bank_account_id: bankAccountsData[0].id }));
        }
      }
      if (withdrawalRes) setWithdrawalRequests(withdrawalRes.data || []);
      if (withdrawalInfoRes) setWithdrawalInfo(withdrawalInfoRes.data || null);

      // Stripe Connect情報も取得
      await fetchStripeConnectAccount();

      // 本人確認状態を取得
      try {
        const { data } = await client.get('/identity-verification/status');
        if (data && typeof data.status !== 'undefined') setIdentityStatus(data.status);
      } catch (error) {
        console.error('Error fetching identity status:', error);
      }

    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 銀行口座登録・更新
  const handleBankAccountSubmit = async (e) => {
    e.preventDefault();

    // バリデーション
    if (!bankForm.bank_name.trim()) {
      toast.error('銀行名を入力してください');
      return;
    }
    if (!bankForm.branch_name.trim()) {
      toast.error('支店名を入力してください');
      return;
    }
    if (!bankForm.account_number.trim()) {
      toast.error('口座番号を入力してください');
      return;
    }
    if (!bankForm.account_holder_name.trim()) {
      toast.error('口座名義を入力してください');
      return;
    }

    // 口座番号の形式チェック（7桁の数字）
    if (!isValidAccountNumber(bankForm.account_number)) {
      toast.error('口座番号は7桁の数字で入力してください');
      return;
    }

    // 口座名義のカナチェック（カタカナのみ）
    if (!isValidKatakana(bankForm.account_holder_name)) {
      toast.error('口座名義はカタカナ（全角）で入力してください\n例: ヤマダタロウ');
      return;
    }

    // 口座照合チェック
    if (!verificationResult || !verificationResult.is_valid) {
      toast.error('口座照合を実行してください。\n「照合する」ボタンをクリックして口座情報を確認してください。');
      return;
    }

    // Transfer fee notice
    if (!window.confirm('振込手数料はご本人の負担となります。よろしいですか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const payload = {
        ...bankForm,
        bank_code: bankForm.bank_code || '',
        branch_code: bankForm.branch_code || ''
      };
      if (editingAccount) {
        await client.put(`/wallet/bank-accounts/${editingAccount.id}`, payload);
      } else {
        await client.post('/wallet/bank-accounts', payload);
      }
      {
        toast.success(editingAccount ? '銀行口座を更新しました' : '銀行口座を登録しました');
        setShowBankForm(false);
        setEditingAccount(null);
        setBankForm({
          bank_name: '',
          bank_code: '',
          branch_name: '',
          branch_code: '',
          account_type: '普通',
          account_number: '',
          account_holder_name: ''
        });
        fetchWalletData();
      }
    } catch (error) {
      console.error('Error saving bank account:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // 銀行口座削除
  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('この銀行口座を削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      await client.delete(`/wallet/bank-accounts/${accountId}`);
      toast.success('銀行口座を削除しました');
      fetchWalletData();
    } catch (error) {
      console.error('Error deleting bank account:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // 振込申請
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) < 1000) {
      toast.error('最低振込金額は1,000円です');
      return;
    }

    if (!withdrawForm.bank_account_id) {
      toast.error('振込先口座を選択してください');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const idemKey = `wd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await client.post('/wallet/withdrawal-request', {
        amount: parseFloat(withdrawForm.amount),
        bank_account_id: withdrawForm.bank_account_id,
      }, {
        headers: { 'X-Idempotency-Key': idemKey }
      });
      toast.success('振込申請を受け付けました。管理者の承認をお待ちください。', {
        duration: 5000
      });

      setShowWithdrawForm(false);
      setWithdrawForm({ amount: '', bank_account_id: withdrawForm.bank_account_id });
      fetchWalletData();
    } catch (error) {
      console.error('Error creating withdrawal request:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // 口座編集開始
  const startEditAccount = (account) => {
    setEditingAccount(account);
    setBankForm({
      bank_name: account.bank_name,
      branch_name: account.branch_name,
      account_type: account.account_type,
      account_number: account.account_number,
      account_holder_name: account.account_holder_name
    });

    // 編集時の状態設定
    setBankSearchQuery(account.bank_name);
    setSelectedBankName(account.bank_name);
    setBranchSearchQuery(account.branch_name);
    setVerificationResult(null);

    // 編集対象の銀行の支店一覧を取得
    if (account.bank_name) {
      searchBranches(account.bank_name, '');
    }

    setShowBankForm(true);
  };

  // 取引タイプのアイコンとラベル
  const getTransactionInfo = (transaction) => {
    switch (transaction.transaction_type) {
      case 'stripe_connect_received':
        return {
          icon: (
            <svg className="h-5 w-5 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
            </svg>
          ),
          label: 'Stripe入金',
          color: 'text-purple-600'
        };
      case 'payment_received':
      case 'reward':
        return {
          icon: <CurrencyYenIcon className="h-5 w-5 text-green-500" />,
          label: '報酬',
          color: 'text-green-600'
        };
      case 'withdrawal_request':
        return {
          icon: <ArrowDownTrayIcon className="h-5 w-5 text-red-500" />,
          label: '振込申請',
          color: 'text-red-600'
        };
      case 'withdrawal_completed':
        return {
          icon: <CheckCircleIcon className="h-5 w-5 text-blue-500" />,
          label: '振込完了',
          color: 'text-blue-600'
        };
      default:
        return {
          icon: <BanknotesIcon className="h-5 w-5 text-gray-500" />,
          label: '取引',
          color: 'text-gray-600'
        };
    }
  };

  // ステータスアイコン
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: <ClockIcon className="h-4 w-4 text-yellow-500" />,
          label: '申請中',
          color: 'text-yellow-600 bg-yellow-100'
        };
      case 'processing':
        return {
          icon: <ClockIcon className="h-4 w-4 text-blue-500" />,
          label: '処理中',
          color: 'text-blue-600 bg-blue-100'
        };
      case 'completed':
        return {
          icon: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
          label: '完了',
          color: 'text-green-600 bg-green-100'
        };
      case 'failed':
        return {
          icon: <XCircleIcon className="h-4 w-4 text-red-500" />,
          label: '失敗',
          color: 'text-red-600 bg-red-100'
        };
      default:
        return {
          icon: <ClockIcon className="h-4 w-4 text-gray-500" />,
          label: 'その他',
          color: 'text-gray-600 bg-gray-100'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <WalletIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">報酬管理</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Stripe Connect未設定の警告バナー */}
        {!stripeConnectAccount?.connected && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-red-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-red-800 font-bold">Stripe Connectの設定が必要です</h3>
                <p className="text-red-700 text-sm mt-1">
                  仕事に応募するには、Stripe Connectアカウントを設定する必要があります。
                  報酬は直接あなたのStripeアカウントに入金されます。
                </p>
                <button
                  onClick={createStripeConnectAccount}
                  disabled={isStripeConnecting}
                  className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isStripeConnecting ? '処理中...' : '今すぐ設定する'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Connect設定完了のバナー */}
        {stripeConnectAccount?.connected && stripeConnectAccount?.payouts_enabled && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-green-800 font-medium">
                  Stripe Connect設定完了 - 報酬は直接あなたのStripeアカウントに入金されます
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ウォレット残高表示 */}
        {stripeConnectAccount?.connected ? (
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg p-6 text-white mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-purple-200 text-sm mb-1">Stripeアカウント残高</p>
                <p className="text-3xl font-bold">
                  ¥{(stripeConnectAccount?.balance?.available || 0).toLocaleString()}
                </p>
              </div>
              <svg className="h-16 w-16 text-purple-300" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-purple-500">
              <div>
                <p className="text-purple-200 text-xs">利用可能残高</p>
                <p className="text-lg font-bold">¥{(stripeConnectAccount?.balance?.available || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-purple-200 text-xs">処理中の残高</p>
                <p className="text-lg font-bold">¥{(stripeConnectAccount?.balance?.pending || 0).toLocaleString()}</p>
              </div>
            </div>
            <p className="text-purple-200 text-xs mt-3">
              ※ 報酬は直接Stripeに入金されます。銀行振込はStripeダッシュボードから設定できます。
            </p>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">内部ウォレット残高（参考）</p>
                <p className="text-3xl font-bold">
                  ¥{wallet ? wallet.balance.toLocaleString() : '0'}
                </p>
                <p className="text-blue-200 text-xs mt-1">
                  ※ Stripe Connectを設定すると、報酬が直接Stripeに入金されます。
                </p>
              </div>
              <WalletIcon className="h-16 w-16 text-blue-200" />
            </div>
          </div>
        )}

        {/* タブナビゲーション */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: '概要', icon: <WalletIcon className="h-4 w-4" /> },
                { id: 'transactions', label: '取引履歴', icon: <BanknotesIcon className="h-4 w-4" /> },
                { id: 'bank-accounts', label: '銀行口座', icon: <CreditCardIcon className="h-4 w-4" /> },
                { id: 'withdrawals', label: '振込申請', icon: <ArrowDownTrayIcon className="h-4 w-4" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* 概要タブ */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 振込申請ボタン */}
                  <div className="bg-green-50 rounded-lg p-6 text-center">
                    <ArrowDownTrayIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">振込申請</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      銀行口座に報酬を振り込み申請できます
                    </p>
                    <button
                      onClick={() => setShowWithdrawForm(true)}
                      disabled={!withdrawalInfo || !withdrawalInfo.can_withdraw}
                      className="bg-green-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      振込申請
                    </button>
                    {withdrawalInfo && !withdrawalInfo.has_bank_account && (
                      <p className="text-red-500 text-xs mt-2">
                        まず銀行口座を登録してください
                      </p>
                    )}
                    {withdrawalInfo && !withdrawalInfo.identity_verified && (
                      <p className="text-red-500 text-xs mt-2">
                        本人確認を完了してください
                      </p>
                    )}
                    {withdrawalInfo && withdrawalInfo.current_balance < withdrawalInfo.min_withdrawal_amount && (
                      <p className="text-red-500 text-xs mt-2">
                        最低出金額: {withdrawalInfo.min_withdrawal_amount.toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* 銀行口座管理 */}
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <CreditCardIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">銀行口座</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      振込先の銀行口座を管理できます
                    </p>
                    <button
                      onClick={() => setActiveTab('bank-accounts')}
                      className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      口座を管理
                    </button>
                  </div>

                  {/* Stripe Connect統合 - 必須 */}
                  <div className={`rounded-lg p-6 text-center ${stripeConnectAccount?.connected ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-300'}`}>
                    <svg className={`h-12 w-12 mx-auto mb-3 ${stripeConnectAccount?.connected ? 'text-green-600' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Stripe Connect <span className="text-red-500 text-sm">（必須）</span>
                    </h3>

                    {stripeConnectAccount?.connected ? (
                      <div>
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4 ${
                          stripeConnectAccount.payouts_enabled
                            ? 'bg-green-100 text-green-800'
                            : stripeConnectAccount.details_submitted
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-orange-100 text-orange-800'
                        }`}>
                          {stripeConnectAccount.payouts_enabled && '✓ 報酬受取準備完了'}
                          {!stripeConnectAccount.payouts_enabled && stripeConnectAccount.details_submitted && '審査中'}
                          {!stripeConnectAccount.payouts_enabled && !stripeConnectAccount.details_submitted && '設定未完了'}
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          {stripeConnectAccount.payouts_enabled
                            ? '仕事の報酬は直接あなたのStripeアカウントに入金されます。'
                            : 'Stripeアカウントの設定を完了してください。'
                          }
                        </p>
                        {!stripeConnectAccount.payouts_enabled && (
                          <button
                            onClick={createStripeConnectAccount}
                            disabled={isStripeConnecting}
                            className="bg-purple-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
                          >
                            {isStripeConnecting ? '処理中...' : '設定を続ける'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4 text-left">
                          <p className="text-red-800 text-sm font-medium mb-2">
                            ⚠️ Stripe Connectの設定が必要です
                          </p>
                          <p className="text-red-700 text-sm">
                            仕事に応募するには、報酬受取用のStripe Connectアカウントが必要です。
                            報酬は直接あなたのStripeアカウントに入金されます。
                          </p>
                        </div>
                        <button
                          onClick={createStripeConnectAccount}
                          disabled={isStripeConnecting}
                          className="bg-red-500 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-600 transition-colors disabled:opacity-50 w-full"
                        >
                          {isStripeConnecting ? '処理中...' : 'Stripe Connectを設定する（必須）'}
                        </button>
                        <p className="text-gray-500 text-xs mt-2">
                          ※ Stripeは世界最大級の決済プラットフォームです。安全に報酬を受け取れます。
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stripe振込履歴 (Stripe Connect設定済みの場合) */}
                {stripeConnectAccount?.connected && stripeConnectAccount?.recent_payouts?.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Stripe振込履歴</h3>
                    <div className="space-y-3">
                      {stripeConnectAccount.recent_payouts.map((payout) => (
                        <div key={payout.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              payout.status === 'paid' ? 'bg-green-500' :
                              payout.status === 'pending' ? 'bg-yellow-500' :
                              payout.status === 'in_transit' ? 'bg-blue-500' : 'bg-gray-500'
                            }`}></div>
                            <div>
                              <p className="font-medium text-gray-900">
                                ¥{payout.amount.toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-500">
                                {payout.status === 'paid' ? '振込完了' :
                                 payout.status === 'pending' ? '処理待ち' :
                                 payout.status === 'in_transit' ? '送金中' : payout.status}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500">
                            {payout.arrival_date ? new Date(payout.arrival_date * 1000).toLocaleDateString('ja-JP') : '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                    <a
                      href="https://dashboard.stripe.com/connect/accounts"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      Stripeダッシュボードで詳細を確認
                      <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}

                {/* 出金情報 (Stripe Connect未設定の場合のみ表示) */}
                {!stripeConnectAccount?.connected && withdrawalInfo && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">出金情報</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">現在残高</p>
                        <p className="font-bold text-lg">{withdrawalInfo.current_balance.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">最低出金額</p>
                        <p className="font-bold">{withdrawalInfo.min_withdrawal_amount.toLocaleString()}</p>
                      </div>
                      {typeof withdrawalInfo.max_withdrawal_amount !== 'undefined' && (
                        <div>
                          <p className="text-gray-600">最大出金可能額</p>
                          <p className="font-bold text-green-600">{withdrawalInfo.max_withdrawal_amount.toLocaleString()}</p>
                        </div>
                      )}
                      {typeof withdrawalInfo.withdrawal_fee_type !== 'undefined' && (
                        <div>
                          <p className="text-gray-600">手数料</p>
                          <p className="font-bold">
                            {withdrawalInfo.withdrawal_fee_type === 'fixed'
                              ? `${withdrawalInfo.withdrawal_fee_amount}`
                              : withdrawalInfo.withdrawal_fee_type === 'percentage'
                                ? `${withdrawalInfo.withdrawal_fee_amount}%`
                                : '無料'
                            }
                          </p>
                        </div>
                      )}
                    </div>

                    {withdrawalInfo.sample_withdrawal_amount && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <strong>例:</strong> {withdrawalInfo.sample_withdrawal_amount.toLocaleString()}を出金する場合
                          <br />
                          手数料: {withdrawalInfo.sample_withdrawal_fee.toLocaleString()}、
                          振込額: {withdrawalInfo.sample_net_amount.toLocaleString()}
                        </p>
                      </div>
                    )}

                    {/* 手数料の明示 */}
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>手数料について:</strong> {withdrawalInfo.fee_notice || '振込手数料は各銀行の規定により求職者のご負担となります。手数料は振込額から差し引かれます。'}
                      </p>
                    </div>
                  </div>
                )}

                {/* 最近の取引 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">最近の取引</h3>
                  {transactions.length > 0 ? (
                    <div className="space-y-3">
                      {transactions.slice(0, 5).map((transaction) => {
                        const info = getTransactionInfo(transaction);
                        return (
                          <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              {info.icon}
                              <div>
                                <p className="font-medium text-gray-900">{transaction.description}</p>
                                <p className="text-sm text-gray-500">
                                  {new Date(transaction.created_at).toLocaleDateString('ja-JP')}
                                </p>
                              </div>
                            </div>
                            <span className={`font-bold ${info.color}`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => setActiveTab('transactions')}
                        className="w-full text-blue-600 text-sm hover:text-blue-800 transition-colors"
                      >
                        すべての取引履歴を見る
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">取引履歴がありません</p>
                  )}
                </div>
              </div>
            )}

            {/* 取引履歴タブ */}
            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">取引履歴</h3>
                {transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.map((transaction) => {
                      const info = getTransactionInfo(transaction);
                      return (
                        <div key={transaction.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {info.icon}
                            <div>
                              <p className="font-medium text-gray-900">{transaction.description}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(transaction.created_at).toLocaleString('ja-JP')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${info.color}`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                            </span>
                            {transaction.status !== 'completed' && (
                              <p className="text-xs text-gray-500 mt-1">
                                {transaction.status === 'pending' ? '処理中' : transaction.status}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">取引履歴がありません</p>
                )}
              </div>
            )}

            {/* 銀行口座タブ */}
            {activeTab === 'bank-accounts' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">銀行口座</h3>
                  <button
                    onClick={() => setShowBankForm(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    口座を追加
                  </button>
                </div>

                {bankAccounts.length > 0 ? (
                  <div className="space-y-3">
                    {bankAccounts.map((account) => (
                      <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-gray-900">{account.bank_name}</h4>
                              {account.is_primary && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                  メイン
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600">{account.branch_name}</p>
                            <p className="text-gray-600">
                              {account.account_type} {account.account_number}
                            </p>
                            <p className="text-gray-600">{account.account_holder_name}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => startEditAccount(account)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAccount(account.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">銀行口座が登録されていません</p>
                    <button
                      onClick={() => setShowBankForm(true)}
                      className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      最初の口座を登録
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 振込申請タブ */}
            {activeTab === 'withdrawals' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">振込申請履歴</h3>
                  <button
                    onClick={() => setShowWithdrawForm(true)}
                    disabled={!wallet || wallet.balance < 1000 || bankAccounts.length === 0}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    新規申請
                  </button>
                </div>

                {withdrawalRequests.length > 0 ? (
                  <div className="space-y-3">
                    {withdrawalRequests.map((request) => {
                      const statusInfo = getStatusInfo(request.status);
                      return (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-bold text-lg">
                                  {request.amount.toLocaleString()}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm">
                                申請日: {new Date(request.requested_at).toLocaleDateString('ja-JP')}
                              </p>
                              {request.processed_at && (
                                <p className="text-gray-600 text-sm">
                                  処理日: {new Date(request.processed_at).toLocaleDateString('ja-JP')}
                                </p>
                              )}
                            </div>
                            {statusInfo.icon}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ArrowDownTrayIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">振込申請履歴がありません</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 銀行口座登録・編集モーダル */}
      {showBankForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[85vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {editingAccount ? '銀行口座を編集' : '銀行口座を登録'}
            </h2>
            <form onSubmit={handleBankAccountSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  銀行名 *
                </label>

                {/* 銀行入力方法の切り替えタブ */}
                <div className="flex mb-2 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setBankInputMode('dropdown')}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${bankInputMode === 'dropdown'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    選択
                  </button>
                  <button
                    type="button"
                    onClick={() => setBankInputMode('hiragana')}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${bankInputMode === 'hiragana'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    ひらがな検索
                  </button>
                  <button
                    type="button"
                    onClick={() => setBankInputMode('search')}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${bankInputMode === 'search'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    手入力
                  </button>
                </div>

                {bankInputMode === 'dropdown' ? (
                  /* プルダウン選択 */
                  <select
                    value={bankForm.bank_name}
                    onChange={(e) => {
                      const selectedBank = e.target.value;
                      setBankForm({ ...bankForm, bank_name: selectedBank, branch_name: '' });
                      setBankSearchQuery(selectedBank);
                      setSelectedBankName(selectedBank);

                      // 支店検索の状態をリセット
                      setBranchSearchQuery('');
                      setBranchSearchResults([]);
                      setShowBranchDropdown(false);
                      setVerificationResult(null);

                      // 選択された銀行の全支店を取得
                      if (selectedBank) {
                        searchBranches(selectedBank, '');
                        // 支店選択モードを自動で「選択」に変更
                        setBranchInputMode('dropdown');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">銀行を選択してください</option>
                    {JAPANESE_BANKS_WITH_CODES.map((bank, index) => (
                      <option key={index} value={bank.name}>
                        {bank.name} ({bank.code})
                      </option>
                    ))}
                  </select>
                ) : bankInputMode === 'hiragana' ? (
                  /* ひらがなサジェスト入力 */
                  <div className="relative">
                    <input
                      type="text"
                      value={bankSearchQuery}
                      onChange={handleBankHiraganaInput}
                      onFocus={() => {
                        if (bankSearchQuery.length > 0) {
                          searchBanksHiragana(bankSearchQuery);
                          setShowBankHiraganaSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowBankHiraganaSuggestions(false), 150);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ひらがなで銀行名を入力（例：みつびしゆーえふじぇー）"
                      required
                    />

                    {/* ひらがなサジェストドロップダウン */}
                    {showBankHiraganaSuggestions && bankHiraganaSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-100 bg-blue-50">
                          <p className="text-xs text-blue-700 font-medium">
                            サジェスト候補 ({bankHiraganaSuggestions.length}件)
                          </p>
                        </div>
                        {bankHiraganaSuggestions.map((bank, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectBankFromHiraganaSuggestion(bank)}
                            className="w-full px-3 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-medium text-gray-900">{bank.name}</span>
                                <div className="text-xs text-gray-500 mt-1">
                                  <span className="bg-gray-100 px-2 py-1 rounded">{bank.hiragana}</span>
                                </div>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {bank.code}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* ひらがな入力説明 */}
                    <p className="text-xs text-blue-600 mt-1">
                      ひらがなで入力すると候補が表示されます（ソニー銀行のような検索）
                    </p>
                  </div>
                ) : (
                  /* 検索機能付きテキスト入力 */
                  <div className="relative">
                    <input
                      type="text"
                      value={bankSearchQuery}
                      onChange={handleBankSearchInput}
                      onFocus={() => {
                        if (bankSearchQuery.length > 0) {
                          searchBanks(bankSearchQuery);
                          setShowBankDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="銀行名を入力してください（例：三菱UFJ銀行）"
                      required
                    />

                    {/* 銀行検索結果ドロップダウン */}
                    {showBankDropdown && bankSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {bankSearchResults.map((bank, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectBank(bank)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          >
                            <div className="flex justify-between items-center">
                              <span>{typeof bank === 'object' ? bank.name : bank}</span>
                              {typeof bank === 'object' && bank.code && (
                                <span className="text-xs text-gray-500">({bank.code})</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 手入力説明 */}
                    <p className="text-xs text-gray-500 mt-1">
                      候補にない銀行名は手入力で登録できます
                    </p>
                  </div>
                )}
              </div>

              {/* 支店名選択（プルダウン＋検索・手入力） */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  支店名 *
                </label>

                {/* 支店入力方法の切り替えタブ */}
                <div className="flex mb-2 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setBranchInputMode('dropdown')}
                    disabled={!selectedBankName}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${branchInputMode === 'dropdown'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      } ${!selectedBankName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    選択
                  </button>
                  <button
                    type="button"
                    onClick={() => setBranchInputMode('hiragana')}
                    disabled={!selectedBankName}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${branchInputMode === 'hiragana'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      } ${!selectedBankName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    ひらがな検索
                  </button>
                  <button
                    type="button"
                    onClick={() => setBranchInputMode('search')}
                    disabled={!selectedBankName}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${branchInputMode === 'search'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      } ${!selectedBankName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    手入力
                  </button>
                </div>

                {branchInputMode === 'dropdown' ? (
                  /* プルダウン選択 */
                  <div className="relative">
                    <select
                      value={bankForm.branch_name}
                      onChange={(e) => {
                        const selectedBranch = e.target.value;
                        setBankForm({ ...bankForm, branch_name: selectedBranch });
                        setBranchSearchQuery(selectedBranch);
                        setVerificationResult(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={!selectedBankName || branchSearchResults.length === 0}
                      required
                    >
                      <option value="">
                        {!selectedBankName
                          ? "まず銀行を選択してください"
                          : branchSearchResults.length === 0
                            ? "支店情報を取得中..."
                            : "支店を選択してください"
                        }
                      </option>
                      {branchSearchResults.map((branch, index) => (
                        <option key={index} value={typeof branch === 'object' ? branch.name : branch}>
                          {typeof branch === 'object' ? `${branch.name} (${branch.code})` : branch}
                        </option>
                      ))}
                    </select>
                    {selectedBankName && branchSearchResults.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedBankName}の支店一覧 ({branchSearchResults.length}件)
                      </p>
                    )}
                  </div>
                ) : branchInputMode === 'hiragana' ? (
                  /* ひらがなサジェスト入力 */
                  <div className="relative">
                    <input
                      type="text"
                      value={branchSearchQuery}
                      onChange={handleBranchHiraganaInput}
                      onFocus={() => {
                        if (selectedBankName) {
                          if (branchSearchQuery.length > 0) {
                            searchBranchesHiragana(selectedBankName, branchSearchQuery);
                          } else {
                            searchBranchesHiragana(selectedBankName, '');
                          }
                          setShowBranchHiraganaSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowBranchHiraganaSuggestions(false), 150);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={selectedBankName ? "ひらがなで支店名を入力（例：しんじゅく）" : "まず銀行を選択してください"}
                      disabled={!selectedBankName}
                      required
                    />

                    {/* ひらがなサジェストドロップダウン */}
                    {showBranchHiraganaSuggestions && selectedBankName && branchHiraganaSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-100 bg-blue-50">
                          <p className="text-xs text-blue-700 font-medium">
                            {selectedBankName}の支店候補 ({branchHiraganaSuggestions.length}件)
                          </p>
                        </div>
                        {branchHiraganaSuggestions.map((branch, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectBranchFromHiraganaSuggestion(branch)}
                            className="w-full px-3 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-medium text-gray-900">{branch.name}</span>
                                {branch.hiragana && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    <span className="bg-gray-100 px-2 py-1 rounded">{branch.hiragana}</span>
                                  </div>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {branch.code}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* ひらがな入力説明 */}
                    <p className="text-xs text-blue-600 mt-1">
                      ひらがなで入力すると候補が表示されます
                    </p>
                  </div>
                ) : (
                  /* 検索機能付きテキスト入力 */
                  <div className="relative">
                    <input
                      type="text"
                      value={branchSearchQuery}
                      onChange={handleBranchSearchInput}
                      onFocus={() => {
                        if (selectedBankName) {
                          if (branchSearchResults.length === 0) {
                            searchBranches(selectedBankName, '');
                          }
                          setShowBranchDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowBranchDropdown(false), 150);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={selectedBankName ? "支店名を入力してください（例：本店営業部）" : "まず銀行を選択してください"}
                      disabled={!selectedBankName}
                      required
                    />

                    {/* 支店検索結果ドロップダウン */}
                    {showBranchDropdown && selectedBankName && branchSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-100 bg-gray-50">
                          <p className="text-xs text-gray-600">
                            {selectedBankName}の支店候補 ({branchSearchResults.length}件)
                          </p>
                        </div>
                        {branchSearchResults.map((branch, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectBranch(branch)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{typeof branch === 'object' ? branch.name : branch}</span>
                              {typeof branch === 'object' && branch.code && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {branch.code}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 手入力説明 */}
                    <p className="text-xs text-gray-500 mt-1">
                      候補にない支店名は手入力で登録できます
                    </p>
                  </div>
                )}

                {/* 銀行未選択時の説明 */}
                {!selectedBankName && (
                  <p className="text-xs text-gray-500 mt-1">
                    まず銀行を選択してください
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座種別 *
                </label>
                <select
                  value={bankForm.account_type}
                  onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="普通">普通</option>
                  <option value="当座">当座</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座番号 *
                </label>
                <input
                  type="text"
                  value={bankForm.account_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 7);
                    setBankForm({ ...bankForm, account_number: value });
                    setVerificationResult(null); // 口座番号変更時に照合結果をリセット
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${bankForm.account_number && !isValidAccountNumber(bankForm.account_number)
                    ? 'border-red-500'
                    : 'border-gray-300'
                    }`}
                  placeholder="例: 1234567"
                  maxLength="7"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">7桁の数字で入力してください</p>
                {bankForm.account_number && !isValidAccountNumber(bankForm.account_number) && (
                  <p className="text-xs text-red-500 mt-1">
                    7桁の数字で入力してください
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  口座名義人（カタカナ） *
                </label>
                <input
                  type="text"
                  value={bankForm.account_holder_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBankForm({ ...bankForm, account_holder_name: value });
                    setVerificationResult(null); // 口座名義変更時に照合結果をリセット
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${bankForm.account_holder_name && !isValidKatakana(bankForm.account_holder_name)
                    ? 'border-red-500'
                    : 'border-gray-300'
                    }`}
                  placeholder="例: ヤマダタロウ"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  カタカナ（全角）で入力してください（濁点・半濁点含む）
                </p>
                {bankForm.account_holder_name && !isValidKatakana(bankForm.account_holder_name) && (
                  <p className="text-xs text-red-500 mt-1">
                    カタカナで入力してください
                  </p>
                )}
              </div>

              {/* 口座照合ボタンと結果 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">口座情報の照合</h4>
                  <button
                    type="button"
                    onClick={verifyAccount}
                    disabled={isVerifying || !bankForm.bank_name || !bankForm.branch_name || !bankForm.account_number || !bankForm.account_holder_name}
                    className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isVerifying ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        照合中...
                      </>
                    ) : (
                      '照合する'
                    )}
                  </button>
                </div>

                {verificationResult && (
                  <div className={`p-3 rounded-lg text-sm ${verificationResult.is_valid
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                    <div className="flex items-center">
                      {verificationResult.is_valid ? (
                        <CheckCircleIcon className="w-5 h-5 mr-2 text-green-600" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 mr-2 text-red-600" />
                      )}
                      <span className="font-medium">
                        {verificationResult.is_valid ? '照合成功' : '照合失敗'}
                      </span>
                    </div>
                    <p className="mt-1">{verificationResult.message}</p>
                  </div>
                )}

                {!verificationResult && (
                  <p className="text-xs text-gray-500">
                    口座情報を入力後、「照合する」ボタンをクリックして口座情報を確認してください。
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={!verificationResult || !verificationResult.is_valid}
                  className="flex-1 bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingAccount ? '更新' : '登録'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBankForm(false);
                    setEditingAccount(null);
                    setBankForm({
                      bank_name: '',
                      branch_name: '',
                      account_type: '普通',
                      account_number: '',
                      account_holder_name: ''
                    });
                    // 検索関連の状態もリセット
                    setBankSearchQuery('');
                    setBankSearchResults([]);
                    setShowBankDropdown(false);
                    setBranchSearchQuery('');
                    setBranchSearchResults([]);
                    setShowBranchDropdown(false);
                    setSelectedBankName('');
                    setVerificationResult(null);
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 振込申請モーダル */}
      {showWithdrawForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">振込申請</h2>
            <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  振込金額 *
                </label>
                <input
                  type="number"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={withdrawalInfo ? withdrawalInfo.min_withdrawal_amount.toString() : "1000"}
                  min={withdrawalInfo ? withdrawalInfo.min_withdrawal_amount : 1000}
                  max={withdrawalInfo && typeof withdrawalInfo.max_withdrawal_amount !== 'undefined' ? withdrawalInfo.max_withdrawal_amount : (wallet ? wallet.balance : 0)}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  最低振込金額: {withdrawalInfo ? withdrawalInfo.min_withdrawal_amount.toLocaleString() : '1,000'}
                  {withdrawalInfo && typeof withdrawalInfo.max_withdrawal_amount !== 'undefined' && (
                    <>
                      {' / '}最大出金可能額: {withdrawalInfo.max_withdrawal_amount.toLocaleString()}
                    </>
                  )}
                </p>

                {/* クイック金額ボタン */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {[1000, 3000, 5000].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setWithdrawForm({ ...withdrawForm, amount: String(amt) })}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      {amt.toLocaleString()} 円
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setWithdrawForm({ ...withdrawForm, amount: String(wallet ? wallet.balance : 0) })}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    全額
                  </button>
                </div>
              </div>

              {/* 手数料と受取金額の計算表示（手数料定義がある場合のみ詳細計算） */}
              {withdrawForm.amount && withdrawalInfo && typeof withdrawalInfo.withdrawal_fee_type !== 'undefined' && parseFloat(withdrawForm.amount) >= withdrawalInfo.min_withdrawal_amount && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">振込詳細</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">振込申請金額:</span>
                      <span className="font-medium">{parseFloat(withdrawForm.amount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">振込手数料:</span>
                      <span className="font-medium text-red-600">
                        {withdrawalInfo.withdrawal_fee_type === 'fixed'
                          ? `-${withdrawalInfo.withdrawal_fee_amount}`
                          : withdrawalInfo.withdrawal_fee_type === 'percentage'
                            ? `-${Math.floor(parseFloat(withdrawForm.amount) * (withdrawalInfo.withdrawal_fee_amount / 100)).toLocaleString()}`
                            : '無料'
                        }
                      </span>
                    </div>
                    <hr className="border-blue-200" />
                    <div className="flex justify-between font-bold text-blue-900">
                      <span>実際の受取金額:</span>
                      <span>
                        {(() => {
                          const amount = parseFloat(withdrawForm.amount);
                          let fee = 0;
                          if (withdrawalInfo.withdrawal_fee_type === 'fixed') {
                            fee = withdrawalInfo.withdrawal_fee_amount;
                          } else if (withdrawalInfo.withdrawal_fee_type === 'percentage') {
                            fee = Math.floor(amount * (withdrawalInfo.withdrawal_fee_amount / 100));
                          }
                          return (amount - fee).toLocaleString();
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* 一般的な手数料注記 */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>手数料について:</strong> {withdrawalInfo?.fee_notice || '振込手数料は各銀行の規定により求職者のご負担となります。'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  振込先口座 *
                </label>
                <select
                  value={withdrawForm.bank_account_id}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">口座を選択してください</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bank_name} {account.branch_name} ({account.account_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>注意事項:</strong><br />
                  振込申請は即日処理されます<br />
                  振込手数料300円が申請金額から差し引かれます<br />
                  申請後のキャンセルはできません
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-500 text-white py-2 rounded-lg font-medium hover:bg-green-600 transition-colors"
                >
                  申請する
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowWithdrawForm(false);
                    setWithdrawForm({ amount: '', bank_account_id: withdrawForm.bank_account_id });
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;